/**
 * Help Context Hooks
 * React Query hooks for fetching help content from Supabase KB
 * Replaces direct imports from helpContent.ts
 */

import { useQuery } from '@tanstack/react-query';
import {
  fetchHelpContext,
  fetchReferenceData,
  searchHelpContentFromDB,
} from '@/services/knowledgeBase.service';
import type { KBArticle } from '@/types/knowledgeBase.types';

/** Shape of the resolved help context used by HelpSystem components */
export interface ResolvedHelpContext {
  pageId: string;
  title: string;
  description: string;
  quickTips: string[];
  faqs: Array<{ question: string; answer: string }>;
  relatedContent: string[];
  roleSpecificTips?: {
    trade?: string[];
    client?: string[];
    admin?: string[];
  };
  troubleshooting?: Array<{
    issue: string;
    steps: string[];
    resolution: string;
  }>;
}

/** Transform a KBArticle with help_context content_type into a ResolvedHelpContext */
function toResolvedHelpContext(article: KBArticle): ResolvedHelpContext {
  return {
    pageId: article.context_ids?.[0] ?? article.slug,
    title: article.title,
    description: article.excerpt ?? '',
    quickTips: article.quick_tips ?? [],
    faqs: article.faqs ?? [],
    relatedContent: article.related_articles ?? [],
    roleSpecificTips: article.role_specific_tips ?? undefined,
    troubleshooting: article.troubleshooting ?? undefined,
  };
}

/**
 * Fetch a single help context by contextId.
 * Returns structured data matching the old HelpContextConfig shape.
 */
export function useHelpContext(contextId: string | undefined) {
  return useQuery({
    queryKey: ['help-context', contextId],
    queryFn: async (): Promise<ResolvedHelpContext | null> => {
      if (!contextId) return null;
      const result = await fetchHelpContext(contextId);
      if (result.error || !result.data) return null;
      return toResolvedHelpContext(result.data);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000,   // 30 minutes
    enabled: !!contextId,
  });
}

/** Reference data types supported */
export type ReferenceDataType = 'document-requirements' | 'rejection-reasons' | 'photography-tips';

/**
 * Fetch structured reference data (document requirements, rejection reasons, photography tips)
 * from KB articles with content_type = 'reference_data'.
 */
export function useHelpReferenceData<T = unknown>(slug: ReferenceDataType) {
  return useQuery({
    queryKey: ['help-reference', slug],
    queryFn: async (): Promise<T | null> => {
      const result = await fetchReferenceData(slug);
      if (result.error || !result.data?.structured_data) return null;
      return result.data.structured_data as T;
    },
    staleTime: 30 * 60 * 1000, // 30 minutes — reference data changes rarely
    gcTime: 60 * 60 * 1000,    // 1 hour
  });
}

/**
 * Search help content via Supabase full-text search.
 * Returns KB articles matching the query.
 */
export function useHelpSearch(query: string, contextId?: string) {
  return useQuery({
    queryKey: ['help-search', query, contextId],
    queryFn: async () => {
      if (!query.trim()) return [];
      const result = await searchHelpContentFromDB(query, contextId);
      if (result.error) return [];
      return result.data ?? [];
    },
    staleTime: 2 * 60 * 1000,
    enabled: query.trim().length >= 2,
  });
}
